import type { Schema } from '@cli-types/core-engine/schema.js'
import type { ActionsGeneratorResult, SelectOptionCtx } from '@cli-types/next-adapter/generators.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import {
  pluralize,
  quotePropertyName,
  singularize,
  toCamelCase,
  toKebabCase,
  toPascalCase
} from '@cli-utils/text.js'
import { flattenFields } from '@core-engine/schema/field-helpers/flattening/flatten-fields.js'
import { getManyToManyFields } from '@core-engine/schema/field-helpers/flattening/get-many-to-many-fields.js'
import { isLayoutField } from '@core-engine/schema/field-type-guards/is-layout-field.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships/is-user-relationship-target.js'
import { resolveSchemaActionDir } from '@next-adapter/config/paths/resolve-schema-action-dir.js'
import { buildAuthorshipAliasBlock } from '@next-adapter/generators/actions/action-helpers/build-authorship-alias-block.js'
import { buildAuthorshipDataFields } from '@next-adapter/generators/actions/action-helpers/build-authorship-data-fields.js'
import { buildAuthorshipJoins } from '@next-adapter/generators/actions/action-helpers/build-authorship-joins.js'
import { buildAuthorshipSelects } from '@next-adapter/generators/actions/action-helpers/build-authorship-selects.js'
import { buildAuthorshipUserType } from '@next-adapter/generators/actions/action-helpers/build-authorship-user-type.js'
import { buildExplicitSelect } from '@next-adapter/generators/actions/action-helpers/build-explicit-select.js'
import { buildFormatActionDateSource } from '@next-adapter/generators/actions/action-helpers/build-format-action-date-source.js'
import { buildReadSelectOptionType } from '@next-adapter/generators/actions/action-helpers/build-read-select-option-type.js'
import { buildRelationshipDataTypes } from '@next-adapter/generators/actions/action-helpers/build-relationship-data-types.js'
import { buildRelationshipSelect } from '@next-adapter/generators/actions/action-helpers/build-relationship-select.js'
import { buildSingleRowMapping } from '@next-adapter/generators/actions/action-helpers/build-single-row-mapping.js'
import { collectReadSelectFields } from '@next-adapter/generators/actions/action-helpers/collect-read-select-fields.js'
import { createAuthorshipFields } from '@next-adapter/generators/actions/action-helpers/create-authorship-fields.js'
import { getInputFieldType } from '@next-adapter/generators/actions/action-helpers/get-input-field-type.js'
import { getReadFieldType } from '@next-adapter/generators/actions/action-helpers/get-read-field-type.js'
import { isAuthorshipFieldName } from '@next-adapter/generators/actions/action-helpers/is-authorship-field-name.js'
import { buildSelectOptionTypes } from '@next-adapter/generators/actions/entity-file-contents/build-select-option-types.js'
import { genCreateSelectOptionContent } from '@next-adapter/generators/actions/entity-file-contents/gen-create-select-option-content.js'
import { genGetSelectOptionsContent } from '@next-adapter/generators/actions/entity-file-contents/gen-get-select-options-content.js'
import { genMarkdownRendererContent } from '@next-adapter/generators/actions/markdown-renderer.js'
import { generateResolveSelectFieldsSource } from '@next-adapter/generators/actions/select-fields-resolver.js'
import { renderSingleActionsSingleActionsRenderer } from '@next-adapter/templates/renderers/generators/actions/single-actions/generate-single-actions.js'
import { getSingleM2MContext } from './get-single-m2m-context.js'

export function generateSingleActions(
  schema: Schema,
  actionsDir: string,
  options: { force?: boolean; nextMajorVersion?: number; outputNamespace?: string } = {}
): ActionsGeneratorResult {
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const tableVar = toCamelCase(schema.name)
  const camelSingular = toCamelCase(singular)
  const kebabSingular = toKebabCase(singular)
  const schemaActionDir = resolveSchemaActionDir(actionsDir, schema.name, options.outputNamespace)

  const dbFields = flattenFields(schema.fields).filter(
    (f) => !(f.type === 'relationship' && f.multiple === true) && !isLayoutField(f.type)
  )
  const m2mFields = getManyToManyFields(schema.fields)
  const hasM2M = m2mFields.length > 0

  const htmlOutputFields = dbFields.filter(
    (f) => (f.type === 'richtext' || f.type === 'markdown') && f.output === 'html'
  )
  const hasHtmlOutput = htmlOutputFields.length > 0
  const creatableSelectFields = dbFields.filter(
    (field) => field.type === 'select' && field.creatable === true
  )
  const hasCreatableSelectFields = creatableSelectFields.length > 0
  const selectFields = collectReadSelectFields(dbFields)
  const hasSelectFields = selectFields.length > 0
  const relationshipFields = dbFields.filter(
    (f) => f.type === 'relationship' && f.relationship && !f.multiple
  )
  const hasRelationships = relationshipFields.length > 0

  const allDbFields = [...dbFields]
  if (!dbFields.some((f) => f.name === 'createdAt')) {
    allDbFields.push({ name: 'createdAt', type: 'timestamp' as const, required: true })
  }
  if (!dbFields.some((f) => f.name === 'updatedAt')) {
    allDbFields.push({ name: 'updatedAt', type: 'timestamp' as const, required: true })
  }
  for (const field of createAuthorshipFields()) {
    if (!dbFields.some((f) => f.name === field.name)) {
      allDbFields.push(field)
    }
  }

  const upsertFields = dbFields.filter(
    (f) =>
      !f.primaryKey &&
      f.name !== 'createdAt' &&
      f.name !== 'updatedAt' &&
      !isAuthorshipFieldName(f.name)
  )

  const dataFields = allDbFields
    .map(
      (f) =>
        `  ${quotePropertyName(f.name)}: ${getReadFieldType(f)}${f.type === 'select' || f.type === 'relationship' || f.required || f.primaryKey ? '' : ' | null'}`
    )
    .join('\n')
  const htmlFieldTypes = htmlOutputFields.map((f) => `  ${f.name}Html: string`).join('\n')
  const m2mFieldTypes = m2mFields.map((f) => `  ${quotePropertyName(f.name)}: string[]`).join('\n')

  const hasTimestampFields =
    allDbFields.some((f) => f.name === 'createdAt') &&
    allDbFields.some((f) => f.name === 'updatedAt')
  const hasFormattedTimestampFields = allDbFields.some((f) => f.name === 'createdAtFormatted')
  const timestampFields =
    hasTimestampFields && !hasFormattedTimestampFields
      ? '\n  createdAtFormatted: string\n  updatedAtFormatted: string'
      : ''

  const upsertInterfaceFields = upsertFields
    .map((f) => `  ${quotePropertyName(f.name)}?: ${getInputFieldType(f)}`)
    .join('\n')
  const m2mUpsertInterfaceFields = m2mFields
    .map((f) => `  ${quotePropertyName(f.name)}?: string[]`)
    .join('\n')
  const allUpsertInterfaceFields = [upsertInterfaceFields, m2mUpsertInterfaceFields]
    .filter(Boolean)
    .join('\n')

  const fieldMeta = upsertFields
    .map((f) => `{ name: '${f.name}', type: '${f.type}', required: ${f.required ?? false} }`)
    .join(',\n    ')

  const cacheTag = `${schema.name}:all`
  const cacheTagsVar = `${tableVar}CacheTags`
  const nextMajor = options.nextMajorVersion ?? 16
  const cacheInvalidationFn = nextMajor >= 16 ? 'updateTag' : 'revalidateTag'
  const auditCategory = schema.name === 'settings' ? 'settings' : 'entity'
  const cacheReadImport =
    nextMajor >= 16
      ? `import { cacheLife, cacheTag } from 'next/cache'`
      : `import { unstable_cacheTag as cacheTag } from 'next/cache'`
  const cacheLifeLine = nextMajor >= 16 ? `\n  cacheLife('max')` : ''
  const singletonId = '00000000-0000-0000-0000-000000000001'
  const selectOptionsCtx: SelectOptionCtx = {
    singular,
    plural,
    Singular,
    camelSingular,
    cacheReadImport,
    cacheLifeLine,
    cacheInvalidationFn,
    creatableSelectFields,
    selectOptionsTableVar: `${tableVar}SelectOptions`
  }

  const relationshipDataTypes = buildRelationshipDataTypes(allDbFields)
  const typesContent = renderSingleActionsSingleActionsRenderer({
    kind: 'renderSingleActionsContentTemplate',
    props: {
      buildAuthorshipUserTypeBuildAuthorshipUserType: [
        buildAuthorshipUserType(),
        buildReadSelectOptionType(selectFields),
        relationshipDataTypes,
        `export interface ${Singular} {\n${dataFields}${htmlFieldTypes ? `\n${htmlFieldTypes}` : ''}${m2mFieldTypes ? `\n${m2mFieldTypes}` : ''}${timestampFields}\n${buildAuthorshipDataFields()}\n}`,
        `export interface ${Singular}UpsertInput {\n${allUpsertInterfaceFields}\n}`,
        `export interface ${Singular}UpsertResult {\n  success: boolean\n  error?: string\n  ${camelSingular}?: ${Singular}\n}`,
        hasCreatableSelectFields ? buildSelectOptionTypes(selectOptionsCtx) : null,
        `export const ${cacheTagsVar} = {\n  all: '${cacheTag}'\n} as const`
      ]
        .filter(Boolean)
        .join('\n\n')
    }
  })

  const authorshipSelects = buildAuthorshipSelects(hasRelationships ? '        ' : '      ')
  const authorshipJoins = buildAuthorshipJoins(tableVar)
  const selectClause = hasRelationships
    ? buildRelationshipSelect(
        allDbFields,
        relationshipFields,
        tableVar,
        htmlOutputFields,
        authorshipSelects,
        authorshipJoins
      )
    : buildExplicitSelect(
        allDbFields,
        tableVar,
        htmlOutputFields,
        authorshipSelects,
        authorshipJoins
      )
  const singleRowMapping = buildSingleRowMapping(
    allDbFields,
    relationshipFields,
    `${Singular}`,
    htmlOutputFields
  )
  const m2mReadQueries = m2mFields
    .map((field) => {
      const { junctionVar, schemaIdCol, relationIdCol } = getSingleM2MContext(schema.name, field)
      return renderSingleActionsSingleActionsRenderer({
        kind: 'renderSingleActionsRowsTemplate',
        props: {
          fieldName: field.name,
          relationIdCol: relationIdCol,
          junctionVar: junctionVar,
          schemaIdCol: schemaIdCol
        }
      })
    })
    .join('\n')
  const m2mAssignments = m2mFields
    .map((field) => {
      const { relationIdCol } = getSingleM2MContext(schema.name, field)
      return `    singleRow.${field.name} = ${field.name}Rows.map((relationRow) => relationRow.${relationIdCol})`
    })
    .join('\n')
  const m2mReadBlock = hasM2M ? `\n${m2mReadQueries}\n${m2mAssignments}` : ''
  const singleRowReturn = hasSelectFields
    ? `const row = result[0]\n    const singleRow = ${singleRowMapping}${m2mReadBlock}\n    const [resolvedSelectRow] = await resolve${Singular}SelectFields([singleRow])\n    return resolvedSelectRow`
    : `const row = result[0]\n    const singleRow = ${singleRowMapping}${m2mReadBlock}\n    return singleRow`
  const relationshipTableImports = relationshipFields
    .filter((field) => !isUserRelationshipTarget(field.relationship))
    .map((field) => toCamelCase(field.relationship!))
  const m2mJunctionImports = m2mFields.map(
    (field) => getSingleM2MContext(schema.name, field).junctionVar
  )
  const getDbImports = Array.from(
    new Set([
      tableVar,
      'user',
      ...relationshipTableImports,
      ...m2mJunctionImports,
      ...(hasCreatableSelectFields ? [`${tableVar}SelectOptions`] : [])
    ])
  ).sort()
  const selectResolutionFunctions = hasSelectFields
    ? `\n${generateResolveSelectFieldsSource({ Singular, selectFields, selectOptionsTableVar: `${tableVar}SelectOptions`, hasCreatableSelectFields, exported: false })}\n`
    : ''

  const getContent = renderSingleActionsSingleActionsRenderer({
    kind: 'renderSingleActionsServerActionTemplate',
    props: {
      join: getDbImports.join(', '),
      cacheReadImport: cacheReadImport,
      cacheTagsVar: cacheTagsVar,
      singular: Singular,
      hasSelectFieldsSelectOptionValue: hasSelectFields ? ', SelectOptionValue' : '',
      authorshipAliasBlock: buildAuthorshipAliasBlock(),
      singletonId: singletonId,
      formatActionDateSource: buildFormatActionDateSource({ exported: false }),
      selectResolutionFunctions: selectResolutionFunctions,
      cacheLifeLine: cacheLifeLine,
      selectClause: selectClause,
      tableVar: tableVar,
      singleRowReturn: singleRowReturn,
      singularText: singular
    }
  })

  const markdownImport = hasHtmlOutput ? `\nimport { renderMarkdownSync } from './markdown'` : ''
  const htmlUpsertBlock = hasHtmlOutput
    ? `\n` +
      htmlOutputFields
        .map(
          (f) =>
            `    if (processedData.${f.name} !== undefined) {\n      processedData.${f.name}Html = renderMarkdownSync(String(processedData.${f.name} || ''))\n    }`
        )
        .join('\n') +
      '\n'
    : ''
  const m2mInputDeclarations = m2mFields
    .map((field) => {
      const { inputIdsVar } = getSingleM2MContext(schema.name, field)
      return `    const ${inputIdsVar} = Array.isArray(input.${field.name}) ? input.${field.name} : []`
    })
    .join('\n')
  const m2mWriteStatements = m2mFields
    .map((field) => {
      const { junctionVar, schemaIdCol, relationIdCol, relationIdVar, inputIdsVar } =
        getSingleM2MContext(schema.name, field)
      return renderSingleActionsSingleActionsRenderer({
        kind: 'renderSingleActionsContentDetailsTemplate',
        props: {
          junctionVar: junctionVar,
          schemaIdCol: schemaIdCol,
          inputIdsVar: inputIdsVar,
          relationIdVar: relationIdVar,
          relationIdCol: relationIdCol
        }
      })
    })
    .join('\n')
  const upsertDbImports = Array.from(new Set([tableVar, ...m2mJunctionImports])).sort()
  const upsertContent = renderSingleActionsSingleActionsRenderer({
    kind: 'renderSingleActionsServerActionSerializedValueTemplate',
    props: {
      join: upsertDbImports.join(', '),
      cacheInvalidationFn: cacheInvalidationFn,
      singular: Singular,
      kebabSingular: kebabSingular,
      cacheTagsVar: cacheTagsVar,
      markdownImport: markdownImport,
      singletonId: singletonId,
      fieldMeta: fieldMeta,
      m2mInputDeclarationsContent: m2mInputDeclarations ? `${m2mInputDeclarations}\n` : '',
      htmlUpsertBlock: htmlUpsertBlock,
      tableVar: tableVar,
      schemaName: schema.name,
      auditCategory: auditCategory,
      stringify: JSON.stringify(schema.label),
      m2mWriteStatementsContent: m2mWriteStatements ? `\n${m2mWriteStatements}\n` : '',
      camelSingular: camelSingular,
      singularText: singular
    }
  })

  const selectOptionTypeExports = hasCreatableSelectFields
    ? `, ${Singular}SelectOption, ${Singular}SelectOptionFieldName, Create${Singular}SelectOptionInput, Create${Singular}SelectOptionResult`
    : ''
  const selectOptionFunctionExports = hasCreatableSelectFields
    ? renderSingleActionsSingleActionsRenderer({
        kind: 'renderSingleActionsContentDetailsBranchTemplate',
        props: {
          singular: Singular,
          kebabSingular: kebabSingular
        }
      })
    : ''
  const barrelContent = renderSingleActionsSingleActionsRenderer({
    kind: 'renderSingleActionsContentDetailsBranchBodyTemplate',
    props: {
      singular: Singular,
      selectOptionTypeExports: selectOptionTypeExports,
      kebabSingular: kebabSingular,
      selectOptionFunctionExports: selectOptionFunctionExports
    }
  })

  const files = [
    { name: 'types.ts', content: typesContent },
    ...(hasHtmlOutput ? [{ name: 'markdown.ts', content: genMarkdownRendererContent() }] : []),
    { name: `get-${kebabSingular}.ts`, content: getContent },
    ...(hasCreatableSelectFields
      ? [
          {
            name: `get-${kebabSingular}-select-options.ts`,
            content: genGetSelectOptionsContent(selectOptionsCtx)
          },
          {
            name: `create-${kebabSingular}-select-option.ts`,
            content: genCreateSelectOptionContent(selectOptionsCtx)
          }
        ]
      : []),
    { name: `upsert-${kebabSingular}.ts`, content: upsertContent },
    { name: 'index.ts', content: barrelContent }
  ]

  return {
    files: files.map((file) => createGeneratedFile(`${schemaActionDir}/${file.name}`, file.content))
  }
}
